import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import { usePortal } from "@ui/hooks/usePortal";
import classNames from "classnames";
import deburr from "lodash.deburr";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { ChevronUpIcon } from "@jti/brand-icons/chevron-up";
import { Container } from "@jti/core-components/components/Container";

import styles from "./AnchorNavigation.module.scss";

export type AnchorNavigationProps = {
  contentElement: HTMLElement | null;
  targetSelector: string;
};

export const createHash = (el: HTMLElement): string => {
  let text = el.textContent ?? "";

  text = text.trim();
  text = text.toLowerCase();
  text = deburr(text); // Remove accents
  text = text.replace(/\s+/g, "-"); // Replace spaces with hyphens
  text = text.replace(/[^\w-]+/g, ""); // Remove all non-word characters

  return text;
};

const isHTMLElement = (node: Element): node is HTMLElement => {
  return node instanceof HTMLElement;
};

const Navigation: FC<AnchorNavigationProps> = ({ contentElement, targetSelector }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTargetIndex, setActiveTargetIndex] = useState<number>(0);

  const [targets, setTargets] = useState<HTMLElement[]>([]);
  useEffect(() => {
    if (contentElement === null) {
      setTargets([]);
      return;
    }

    const targets: HTMLElement[] = Array.from(contentElement.querySelectorAll(targetSelector))
      .filter(isHTMLElement)
      .filter((el) => el.textContent?.trim());

    setTargets(targets);
  }, [contentElement, targetSelector, setTargets]);

  const changeActiveTarget = useCallback(
    (target: HTMLElement) => {
      const index = targets.indexOf(target);
      setActiveTargetIndex(index);
      setIsExpanded(false);

      target.scrollIntoView({ behavior: "smooth" });
    },
    [targets, setActiveTargetIndex]
  );

  useEffect(() => {
    if (!window.location.hash || window.location.hash === "#") {
      return;
    }

    const target = targets.find((el) => createHash(el) === window.location.hash.slice(1));
    if (target) {
      changeActiveTarget(target);
    }
  }, [targets, changeActiveTarget]);

  const handleAnchorClick = (target: HTMLElement) => {
    changeActiveTarget(target);
    window.location.hash = createHash(target);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const activeTargetText = targets[activeTargetIndex]?.textContent ?? "";

  if (!targets.length) {
    return null;
  }

  return (
    <nav className={styles.nav}>
      {targets.length > 0 && (
        <button type="button" className={styles.toggleButton} onClick={handleToggle}>
          <Container>
            <span className={styles.toggleButtonInner}>
              <span className={styles.label}>{activeTargetText}</span>
              {isExpanded ? (
                <ChevronUpIcon width="20px" height="20px" />
              ) : (
                <ChevronDownIcon width="20px" height="20px" />
              )}
            </span>
          </Container>
        </button>
      )}
      <ul
        className={classNames(styles.list, {
          [styles.expanded]: isExpanded,
        })}
      >
        {targets.map((target, index) => (
          <li key={index} className={classNames(styles.item)}>
            <a
              className={styles.link}
              href={`#${createHash(target)}`}
              onClick={(ev) => {
                ev.preventDefault();
                handleAnchorClick(target);
              }}
            >
              <Container>{target.textContent}</Container>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export const AnchorNavigation: FC<AnchorNavigationProps> = (props) => {
  const { mainBreakpoint } = useCurrentBreakpoint();
  const isMobile = useMemo(() => ["xs", "sm"].includes(mainBreakpoint), [mainBreakpoint]);

  const mobilePortalContainer = usePortal("header");

  return isMobile && mobilePortalContainer ? (
    createPortal(<Navigation {...props} />, mobilePortalContainer)
  ) : (
    <Navigation {...props} />
  );
};
